import { remToPixels } from '../../utilities/utils';

import { gsap } from 'gsap';
import Headroom from 'headroom.js';

interface headerOptions {
  toggle: string;
  overlay: string;
  showItems?: string;
  fadeInItems?: string;
}
export default class Header {
  constructor(root: HTMLElement, options: headerOptions) {
    this.el = root; // 'js-header'

    const siteRoot = document.documentElement;
    const siteBody = document.body;
    const toggleMenu = root.querySelector<HTMLButtonElement>(options.toggle);
    const mainNav = root.querySelector<HTMLElement>(options.overlay);
    const fadeInMoveItems = mainNav.querySelectorAll(options.showItems);
    const fadeInItems = mainNav.querySelectorAll(options.fadeInItems);

    // Setup the default options for headroom.js
    const headroomOptions = {
      classes: {
        initial: 'header',
        pinned: 'header--pinned',
        unpinned: 'header--unpinned',
        top: 'header--top',
        notTop: 'header--not-top',
        bottom: 'header--bottom',
        notBottom: 'header--not-bottom',
        frozen: 'header--frozen',
      },

      // Extra classes on document Root
      onTop: () => siteRoot.classList.remove('header-not-top'),
      onNotTop: () => siteRoot.classList.add('header-not-top'),
      onPin: () => siteRoot.classList.add('header-pinned'),
      onUnpin: () => siteRoot.classList.remove('header-pinned'),
    };

    const headroom = new Headroom(root, headroomOptions);

    let open: Boolean;
    let isAnimating: Boolean = false;

    // Setup the gsap animations
    const tl = gsap
      .timeline({
        paused: true,
        reversed: true,
        onReverseComplete: () => {
          isAnimating = false;
          siteBody.classList.remove('no-scroll');
          mainNav.hidden = true;
        },
        onComplete: () => {
          isAnimating = false;
        },
        onStart: () => {
          siteBody.classList.add('no-scroll');
          mainNav.hidden = false;
        },
      })

      // Hide topnav items
      .fromTo(
        '.js-hide-item',
        { opacity: 1 },
        {
          opacity: 0,
          ease: 'power1.inOut',
          duration: 0.25,
        }
      )

      // Main Nav clipPath animation
      // path-1
      .fromTo(
        mainNav,
        { '--path-1': 0 },
        {
          '--path-1': 100,
          ease: 'power1.inOut',
          duration: 0.4,
        },
        0.15
      )
      // path-2
      .fromTo(
        mainNav,
        { '--path-2': 0 },
        {
          '--path-2': 100,
          ease: 'power1.inOut',
          duration: 0.4,
        },
        0.3
      )
      // path-3
      .fromTo(
        mainNav,
        { '--path-3': 0 },
        {
          '--path-3': 100,
          ease: 'power1.inOut',
          duration: 0.4,
        },
        0.45
      )
      // path-4
      .fromTo(
        mainNav,
        { '--path-4': 0 },
        {
          '--path-4': 100,
          ease: 'power1.inOut',
          duration: 0.4,
        },
        0.6
      );

    if (options.showItems) {
      tl.fromTo(
        fadeInMoveItems,
        { opacity: 0, y: -100 },
        {
          opacity: 1,
          y: 0,
          ease: 'power1.inOut',
          duration: 0.25,
          stagger: 0.2,
        },
        0.4
      );
    }

    if (options.fadeInItems) {
      tl.fromTo(
        fadeInItems,
        { opacity: 0 },
        {
          opacity: 1,
          ease: 'power1.inOut',
          duration: 0.25,
          stagger: 0.2,
        },
        1
      );
    }

    const menuToggle = () => {
      if (isAnimating) return;

      isAnimating = true;

      open = JSON.parse(toggleMenu.getAttribute('aria-expanded'));

      toggleMenu.setAttribute('aria-expanded', new Boolean(!open).toString());

      tl.reversed() ? tl.play() : tl.reverse();
    };

    const setOffsetValues = () => {
      let headerOffset = remToPixels(getComputedStyle(siteRoot).getPropertyValue('--header-height'));
      let heroOffset = remToPixels(getComputedStyle(siteRoot).getPropertyValue('--hero-height'));

      headroom.offset.down = headerOffset;
      headroom.offset.up = heroOffset + headerOffset;
    };

    const registerEventListeners = () => {
      toggleMenu.addEventListener('click', menuToggle);

      window.addEventListener('resize', setOffsetValues);
      window.addEventListener('orientationchange', setOffsetValues);
    };

    const init = () => {
      if (!root) return;

      setOffsetValues();
      registerEventListeners();

      headroom.init();
    };

    init();

    if (Header.instances.has(this.el)) return;

    this.init();
  }

  el: HTMLElement;

  static instances = new WeakMap();

  init() {
    // Save a reference to the instance, so we know this DOM node has already been instancied
    Header.instances.set(this.el, this);
  }

  /**
   * Delete Header instance from DOM element
   */
  unmount() {
    Header.instances.delete(this.el);
  }
}
